import { FC } from "react";

import Stack from "@mui/material/Stack";
import { UseFieldArrayReturn } from "react-hook-form";
import { v4 } from "uuid";

import { MemberInfoBlock } from "../MemberInfoBlock";
import AddGroupMemberIcon from "./addGroupMember.svg";

import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { QuestionOutput } from "@/common/types/trpc";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormChip } from "@/forms-design-system/FormChip";
import { FormDivider } from "@/forms-design-system/FormDivider";
import { Add } from "@/icons/outlined";

interface GroupMembershipMembersProps {
  rateId: string;
  title: string;
  maximumMembers: number;
  groupCount: number;
  questions: QuestionOutput[];
  participants: UseFieldArrayReturn<TicketingPaymentInput, "customAnswersOfParticipants">;
  onAddGroup: () => void;
  canAddGroup: boolean;
  canAddMember?: boolean;
}

export const GroupMembershipMembers: FC<GroupMembershipMembersProps> = ({
  rateId,
  title,
  maximumMembers,
  groupCount,
  questions,
  participants,
  onAddGroup,
  canAddGroup,
  canAddMember = true,
}) => {
  const { t } = useTranslate();
  const rateParticipants = participants.fields.flatMap((participant, index) =>
    participant.rateId === rateId ? [{ ...participant, index }] : []
  );

  return (
    <Stack gap={2.5} data-test="group-membership-members">
      {Array.from({ length: groupCount }, (_, groupIndex) => {
        const members = rateParticipants.filter((member) => (member.membershipGroupIndex ?? 0) === groupIndex);
        const slotsLeft = maximumMembers - members.length;
        return (
          <Stack gap={2.5} key={groupIndex} data-test="membership-group">
            {groupIndex > 0 && <FormDivider sx={{ mb: -0.125 }} />}
            {members.map((member, memberIndex) => (
              <MemberInfoBlock
                key={member.id}
                participantIndex={member.index}
                memberNumber={memberIndex + 1}
                memberCount={members.length}
                questions={questions}
                offersBuyerChoice={member.index === 0}
                isForBuyerDefault={member.index === 0}
                fieldIdPrefix={`rate-${rateId}-group-${groupIndex}`}
                label={t("ticketing", groupCount > 1 ? "groupMembership.memberInGroup" : "groupMembership.member", {
                  title,
                  group: groupIndex + 1,
                  count: memberIndex + 1,
                })}
                onRemove={memberIndex > 0 ? () => participants.remove(member.index) : undefined}
                requireIdentity
              />
            ))}
            <Stack alignItems="flex-start" gap={2.5}>
              {slotsLeft > 0 ? (
                <FormButton
                  data-test="add-group-member"
                  variant="outline"
                  size="small"
                  disabled={!canAddMember}
                  startIcon={<AddGroupMemberIcon />}
                  sx={(theme) => ({ px: 1.875, "& svg": { color: theme.palette.text.neutral.moderate } })}
                  onClick={() => {
                    const lastMember = members[members.length - 1];
                    if (!lastMember) {
                      return;
                    }
                    participants.insert(
                      lastMember.index + 1,
                      {
                        rateId,
                        membershipGroupIndex: groupIndex,
                        isForBuyer: false,
                        answers: questions.map((question) => ({
                          id: v4(),
                          questionId: question.id,
                          type: question.type,
                          choiceIds: [],
                          answers: [""],
                          answer: "",
                        })),
                      },
                      { shouldFocus: false }
                    );
                  }}
                >
                  {t("ticketing", "groupMembership.addMember", { count: slotsLeft })}
                </FormButton>
              ) : (
                <FormChip
                  sx={(theme) => ({ height: theme.spacing(3.75) })}
                  variant="filled"
                  label={t("ticketing", "groupMembership.full", {
                    title,
                    group: groupIndex + 1,
                    count: members.length,
                    maximum: maximumMembers,
                  })}
                />
              )}
              {slotsLeft === 0 && groupIndex === groupCount - 1 && canAddGroup && (
                <>
                  <FormDivider sx={{ width: "100%", mb: -0.125 }} />
                  <FormButton
                    data-test="add-membership-group"
                    variant="outline"
                    size="small"
                    startIcon={<Add />}
                    sx={(theme) => ({ px: 1.875, "& svg": { color: theme.palette.text.neutral.moderate } })}
                    onClick={onAddGroup}
                  >
                    {t("ticketing", "groupMembership.addGroup", { title })}
                  </FormButton>
                </>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};
